import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlane,
  faChevronUp,
  faChevronDown,
  faUser,
  faPoundSign,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";
import { config } from "../../config";
import ProgressBar from "../../components/flights/ProgressBar";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import FlightDetailsModal from "../../components/flights/FlightDetailsModal";
import { format, parseISO } from "date-fns";
import { calculateFinalPrice, formatPrice } from "../../utils/pricing";

function BookingDetails() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [flight, setFlight] = useState(null);
  const [isFareRulesOpen, setIsFareRulesOpen] = useState(false);
  const [isFlightModalOpen, setIsFlightModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    passengers: [],
  });
  const [errors, setErrors] = useState({});

  const formatPhoneNumber = (value) => {
    let cleaned = value.replace(/[^\d+]/g, "");

    if (!cleaned.startsWith("+")) {
      cleaned = "+" + cleaned;
    }

    return cleaned;
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^\+\d{10,15}$/;
    return phoneRegex.test(phone);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  useEffect(() => {
    if (searchParams.get("flight")) {
      const flightData = searchParams.get("flight");
      const decodedData = JSON.parse(decodeURIComponent(flightData));
      const selectedOffer = decodedData.selectedOffer;
      const passengers = selectedOffer.data.passengers;

      setFlight(decodedData);

      setFormData({
        passengers: passengers.map((passenger) => ({
          id: passenger.id,
          given_name: "",
          family_name: "",
          email: "",
          phone: "",
          gender: "",
          born_on: "",
          title: "",
          type: passenger.type,
          firstName: "",
          lastName: "",
          dob: null,
        })),
      });

      console.log("Selected offer data:", decodedData);
    }
  }, [searchParams]);

  if (!flight) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-lg">Loading...</p>
      </div>
    );
  }

  const handleContinue = () => {
    const newErrors = {};
    formData.passengers.forEach((passenger, index) => {
      const passengerType = passenger.type || "adult";
      console.log(`Validating passenger ${index}:`, {
        type: passengerType,
        dob: passenger.dob,
      });

      // Common validations
      if (!passenger.title)
        newErrors[`passenger${index}Title`] = "Title is required";
      if (!passenger.firstName)
        newErrors[`passenger${index}FirstName`] = "First name is required";
      if (!passenger.lastName)
        newErrors[`passenger${index}LastName`] = "Last name is required";
      if (!passenger.dob)
        newErrors[`passenger${index}Dob`] = "Date of birth is required";
      if (!passenger.gender)
        newErrors[`passenger${index}Gender`] = "Gender is required";

      // Lead passenger validations
      if (index === 0) {
        if (!passenger.email)
          newErrors[`passenger${index}Email`] = "Email is required";
        if (passenger.email && !validateEmail(passenger.email))
          newErrors[`passenger${index}Email`] = "Invalid email format";
        if (!passenger.phone)
          newErrors[`passenger${index}Phone`] = "Phone number is required";
        if (passenger.phone && !validatePhone(passenger.phone))
          newErrors[`passenger${index}Phone`] = "Invalid phone number format";
      }

      // Age validations
      if (passenger.dob) {
        const dob = new Date(passenger.dob);
        const today = new Date();
        let age = today.getFullYear() - dob.getFullYear();
        const monthDiff = today.getMonth() - dob.getMonth();

        if (
          monthDiff < 0 ||
          (monthDiff === 0 && today.getDate() < dob.getDate())
        ) {
          age--;
        }

        switch (passengerType) {
          case "infant_without_seat":
          case "infant_with_seat":
            if (age >= 2)
              newErrors[`passenger${index}Dob`] =
                "Infant must be under 2 years old";
            break;
          case "child":
            if (age < 2 || age >= 12)
              newErrors[`passenger${index}Dob`] =
                "Child must be between 2 and 11 years old";
            break;
          case "adult":
            if (age < 12)
              newErrors[`passenger${index}Dob`] =
                "Adult must be 12 years or older";
            break;
          default:
            if (age < 12)
              newErrors[`passenger${index}Dob`] =
                "Passenger must be 12 years or older";
            break;
        }
      }
    });

    if (Object.keys(newErrors).length === 0) {
      const bookingData = {
        selectedOffer: flight.selectedOffer,
        passengers: formData.passengers.map((passenger) => ({
          ...passenger,
          type: passenger.type,
        })),
      };

      console.log("Data being sent to BookingSummary:", bookingData);

      navigate(
        `/flights/summary?data=${encodeURIComponent(
          JSON.stringify(bookingData)
        )}`
      );
    } else {
      setErrors(newErrors);
    }
  };

  const handleInputChange = (index, field, value) => {
    const newPassengers = [...formData.passengers];
    switch (field) {
      case "phone":
        newPassengers[index][field] = formatPhoneNumber(value);
        break;
      case "email":
        newPassengers[index][field] = value.toLowerCase();
        break;
      case "firstName":
      case "lastName":
        newPassengers[index][field] = value.trim();
        break;
      case "title":
      case "gender":
      case "dob":
        newPassengers[index][field] = value;
        break;
      default:
        newPassengers[index][field] = value;
        break;
    }

    setFormData({
      ...formData,
      passengers: newPassengers,
    });

    // Clear error when user starts typing
    if (errors[`passenger${index}${field}`]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`passenger${index}${field}`];
        return newErrors;
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container pb-4 mx-auto px-4 md:px-40 lg:px-40">
        <div className="flex justify-between py-6 items-center border-b">
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              {flight.selectedOffer.data.slices[0].origin.city_name ||
                flight.selectedOffer.data.slices[0].origin.iata_code}
              <FontAwesomeIcon icon={faPlane} size="sm" />{" "}
              {flight.selectedOffer.data.slices[0].destination.city_name ||
                flight.selectedOffer.data.slices[0].destination.iata_code}
            </h1>
            <p className="text-gray-600">
              {
                flight.selectedOffer.data.slices[0].segments[0].departing_at.split(
                  "T"
                )[0]
              }
              {flight.selectedOffer.data.slices[1] &&
                ` • ${
                  flight.selectedOffer.data.slices[1].segments[0].departing_at.split(
                    "T"
                  )[0]
                }`}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xl font-bold">
              {formatPrice(
                calculateFinalPrice(flight.selectedOffer.data.total_amount),
                flight.selectedOffer.data.total_currency
              )}
            </p>
            <p className="text-gray-600">Total price</p>
          </div>
        </div>
        <ProgressBar currentStep={1} />
        <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
          <button
            className="w-full flex justify-between items-center mb-6"
            onClick={() => setIsFlightModalOpen(true)}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center">
                <img
                  src={flight.selectedOffer.data.owner.logo_symbol_url}
                  alt={flight.selectedOffer.data.owner.name}
                  className="w-10 h-10"
                />
              </div>
              <div className="text-left">
                <h2 className=" font-semibold">
                  {flight.selectedOffer.data.owner.name}
                </h2>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-end gap-0 flex-col">
                <p className="text-lg font-bold">
                  {formatPrice(
                    calculateFinalPrice(flight.selectedOffer.data.total_amount),
                    flight.selectedOffer.data.total_currency
                  )}
                </p>
                <p className="text-xs text-gray-500">Total Price</p>
              </div>
            </div>
          </button>

          {/* Outbound flight (slice 0) */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex flex-col items-start">
              <span className="text-lg font-semibold">
                {format(
                  parseISO(
                    flight.selectedOffer.data.slices[0].segments[0].departing_at
                  ),
                  "HH:mm"
                )}
              </span>
              <span className="text-gray-600">
                {flight.selectedOffer.data.slices[0].origin.name} (
                {flight.selectedOffer.data.slices[0].origin.iata_code})
              </span>
            </div>
            <div className="flex-1 px-8">
              <div className="flex flex-col items-center">
                <span className="text-gray-500 text-xs">
                  {flight.selectedOffer.data.slices[0].segments.length === 1
                    ? "Direct"
                    : `${
                        flight.selectedOffer.data.slices[0].segments.length - 1
                      } stop${
                        flight.selectedOffer.data.slices[0].segments.length -
                          1 >
                        1
                          ? "s"
                          : ""
                      }`}
                </span>
                <div className="w-full flex items-center gap-2">
                  <div className="h-[2px] flex-1 bg-gray-300"></div>
                  <FontAwesomeIcon icon={faPlane} className="text-gray-400" />
                  <div className="h-[2px] flex-1 bg-gray-300"></div>
                </div>
              </div>
            </div>
            <div className="flex flex-col items-end">
              <span className="text-lg font-semibold">
                {format(
                  parseISO(
                    flight.selectedOffer.data.slices[0].segments[
                      flight.selectedOffer.data.slices[0].segments.length - 1
                    ].arriving_at
                  ),
                  "HH:mm"
                )}
              </span>
              <span className="text-gray-600">
                {flight.selectedOffer.data.slices[0].destination.name} (
                {flight.selectedOffer.data.slices[0].destination.iata_code})
              </span>
            </div>
          </div>

          {/* Return flight (slice 1), if it exists */}
          {flight.selectedOffer.data.slices[1] && (
            <div className="flex items-center justify-between">
              <div className="flex flex-col items-start">
                <span className="text-lg font-semibold">
                  {format(
                    parseISO(
                      flight.selectedOffer.data.slices[1].segments[0]
                        .departing_at
                    ),
                    "HH:mm"
                  )}
                </span>
                <span className="text-gray-600">
                  {flight.selectedOffer.data.slices[1].origin.name} (
                  {flight.selectedOffer.data.slices[1].origin.iata_code})
                </span>
              </div>
              <div className="flex-1 px-8">
                <div className="flex flex-col items-center">
                  <span className="text-gray-500 text-xs">
                    {flight.selectedOffer.data.slices[1].segments.length === 1
                      ? "Direct"
                      : `${
                          flight.selectedOffer.data.slices[1].segments.length -
                          1
                        } stop${
                          flight.selectedOffer.data.slices[1].segments.length -
                            1 >
                          1
                            ? "s"
                            : ""
                        }`}
                  </span>
                  <div className="w-full flex items-center gap-2">
                    <div className="h-[2px] flex-1 bg-gray-300"></div>
                    <FontAwesomeIcon
                      icon={faPlane}
                      className="text-gray-400 transform rotate-180"
                    />
                    <div className="h-[2px] flex-1 bg-gray-300"></div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-end">
                <span className="text-lg font-semibold">
                  {format(
                    parseISO(
                      flight.selectedOffer.data.slices[1].segments[
                        flight.selectedOffer.data.slices[1].segments.length - 1
                      ].arriving_at
                    ),
                    "HH:mm"
                  )}
                </span>
                <span className="text-gray-600">
                  {flight.selectedOffer.data.slices[1].destination.name} (
                  {flight.selectedOffer.data.slices[1].destination.iata_code})
                </span>
              </div>
            </div>
          )}

          <div className="mt-6 text-sm flex justify-end">
            <button
              className="bg-gray-100 text-gray-700 px-6 py-2 rounded-full flex items-center gap-2"
              onClick={() => setIsFlightModalOpen(true)}
            >
              Trip Details
              <FontAwesomeIcon icon={faCopy} />
            </button>
          </div>
        </div>

        {/* Fare Rules */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <button
            className="w-full p-6 flex justify-between items-center"
            onClick={() => setIsFareRulesOpen(!isFareRulesOpen)}
          >
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                <FontAwesomeIcon
                  icon={faPlane}
                  className={`${config.primaryColorText}`}
                />
              </div>
              <div className="text-left">
                <h2 className="text-lg font-semibold">Fare rules</h2>
                <p className="text-gray-600">Review airline fare rules</p>
              </div>
            </div>
            <FontAwesomeIcon
              icon={isFareRulesOpen ? faChevronUp : faChevronDown}
              className="text-gray-400"
            />
          </button>

          {isFareRulesOpen && (
            <div className="p-6 border-t">
              <div className="space-y-6">
                {/* Changes before departure */}
                <div className="flex items-start gap-4">
                  <div
                    className={`p-2 rounded-full ${
                      flight.selectedOffer.data.conditions
                        .change_before_departure?.allowed
                        ? "bg-green-100"
                        : "bg-red-100"
                    }`}
                  >
                    <FontAwesomeIcon
                      icon={faPlane}
                      className={
                        flight.selectedOffer.data.conditions
                          .change_before_departure?.allowed
                          ? "text-green-600"
                          : "text-red-600"
                      }
                    />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">
                      Changes before departure
                    </h3>
                    {flight.selectedOffer.data.conditions
                      .change_before_departure ? (
                      flight.selectedOffer.data.conditions
                        .change_before_departure.allowed ? (
                        <p className="text-gray-600">
                          Changes are allowed with a fee of{" "}
                          {
                            flight.selectedOffer.data.conditions
                              .change_before_departure.penalty_currency
                          }{" "}
                          {
                            flight.selectedOffer.data.conditions
                              .change_before_departure.penalty_amount
                          }
                        </p>
                      ) : (
                        <p className="text-gray-600">
                          Changes are not permitted before departure
                        </p>
                      )
                    ) : (
                      <p className="text-gray-600">
                        Please view the conditions of carriage link below for
                        change conditions
                      </p>
                    )}
                  </div>
                </div>

                {/* Refunds before departure */}
                <div className="flex items-start gap-4">
                  <div
                    className={`p-2 rounded-full ${
                      flight.selectedOffer.data.conditions
                        .refund_before_departure?.allowed
                        ? "bg-green-100"
                        : "bg-red-100"
                    }`}
                  >
                    <FontAwesomeIcon
                      icon={faPoundSign}
                      className={
                        flight.selectedOffer.data.conditions
                          .refund_before_departure?.allowed
                          ? "text-green-600"
                          : "text-red-600"
                      }
                    />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">
                      Refunds before departure
                    </h3>
                    {flight.selectedOffer.data.conditions
                      .refund_before_departure ? (
                      flight.selectedOffer.data.conditions
                        .refund_before_departure.allowed ? (
                        <p className="text-gray-600">
                          Refunds are allowed with a fee of{" "}
                          {
                            flight.selectedOffer.data.conditions
                              .refund_before_departure.penalty_currency
                          }{" "}
                          {
                            flight.selectedOffer.data.conditions
                              .refund_before_departure.penalty_amount
                          }
                        </p>
                      ) : (
                        <p className="text-gray-600">
                          Refunds are not permitted before departure
                        </p>
                      )
                    ) : (
                      <p className="text-gray-600">
                        Please view the conditions of carriage link below for
                        refund conditions
                      </p>
                    )}
                  </div>
                </div>

                {/* Additional information with conditions of carriage link */}
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">
                    * These fare conditions are provided by the airline. For
                    full details, please review the{" "}
                    {flight.selectedOffer.data.owner
                      .conditions_of_carriage_url ? (
                      <a
                        href={
                          flight.selectedOffer.data.owner
                            .conditions_of_carriage_url
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        conditions of the airline
                      </a>
                    ) : (
                      "conditions of carriage"
                    )}
                    .
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Passenger Details */}
        {formData.passengers.map((passenger, index) => (
          <div key={index} className="bg-white rounded-lg p-6 mb-6 shadow-sm">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                  <FontAwesomeIcon
                    icon={faUser}
                    className="text-yellow-500"
                    size="lg"
                  />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">Passenger details</h2>
                  <p className="text-gray-500">
                    {flight.selectedOffer.data.passengers[index].type.replace(
                      /_/g,
                      " "
                    ) || "Adult"}{" "}
                    {index + 1}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              {/* Title field */}
              <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                <select
                  name="title"
                  value={passenger.title}
                  onChange={(e) =>
                    handleInputChange(index, "title", e.target.value)
                  }
                  className="w-full h-14 px-4 text-gray-700 bg-white rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Title</option>
                  <option value="mr">Mr</option>
                  <option value="mrs">Mrs</option>
                  <option value="ms">Ms</option>
                  <option value="miss">Miss</option>
                  <option value="dr">Dr</option>
                </select>
                {errors[`passenger${index}Title`] && (
                  <p className="text-red-500 text-sm">
                    {errors[`passenger${index}Title`]}
                  </p>
                )}
              </div>

              {/* First name field */}
              <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                <input
                  type="text"
                  name="firstName"
                  placeholder="First name"
                  value={passenger.firstName}
                  onChange={(e) =>
                    handleInputChange(index, "firstName", e.target.value)
                  }
                  className="w-full h-14 px-4 text-gray-700 bg-white rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                {errors[`passenger${index}FirstName`] && (
                  <p className="text-red-500 text-sm">
                    {errors[`passenger${index}FirstName`]}
                  </p>
                )}
              </div>

              {/* Last name field */}
              <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                <input
                  type="text"
                  name="lastName"
                  placeholder="Last name"
                  value={passenger.lastName}
                  onChange={(e) =>
                    handleInputChange(index, "lastName", e.target.value)
                  }
                  className="w-full h-14 px-4 text-gray-700 bg-white rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                {errors[`passenger${index}LastName`] && (
                  <p className="text-red-500 text-sm">
                    {errors[`passenger${index}LastName`]}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                <DatePicker
                  selected={passenger.dob}
                  onChange={(date) => handleInputChange(index, "dob", date)}
                  placeholderText="Date of birth"
                  className={`w-full h-18 p-4 text-lg rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:${config.primaryColorRing}`}
                  showYearDropdown
                  dropdownMode="select"
                  dateFormat="dd/MM/yyyy"
                  isClearable
                  peekNextMonth
                  showMonthDropdown
                  useShortMonthInDropdown
                  yearDropdownItemNumber={100}
                  scrollableYearDropdown
                  openToDate={new Date("1990-01-01")}
                />
                {errors[`passenger${index}Dob`] && (
                  <p className="text-red-500">
                    {errors[`passenger${index}Dob`]}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                <select
                  name="gender"
                  value={passenger.gender}
                  onChange={(e) =>
                    handleInputChange(index, "gender", e.target.value)
                  }
                  className={`w-full h-18 p-4 text-lg rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:${config.primaryColorRing}`}
                >
                  <option value="">Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
                {errors[`passenger${index}Gender`] && (
                  <p className="text-red-500">
                    {errors[`passenger${index}Gender`]}
                  </p>
                )}
              </div>

              {/* Email and phone fields only for the first passenger */}
              {index === 0 && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                    <input
                      type="email"
                      name="email"
                      placeholder="Email"
                      value={passenger.email}
                      onChange={(e) =>
                        handleInputChange(index, "email", e.target.value)
                      }
                      className={`w-full h-18 p-4 text-lg rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:${config.primaryColorRing}`}
                    />
                    {errors[`passenger${index}Email`] && (
                      <p className="text-red-500 text-sm">
                        {errors[`passenger${index}Email`]}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Phone"
                      value={passenger.phone}
                      onChange={(e) =>
                        handleInputChange(index, "phone", e.target.value)
                      }
                      className={`w-full h-18 p-4 text-lg rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:${config.primaryColorRing}`}
                    />
                    {errors[`passenger${index}Phone`] && (
                      <p className="text-red-500 text-sm">
                        {errors[`passenger${index}Phone`]}
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
        <button
          onClick={handleContinue}
          className={`w-full ${config.primaryColorBg} text-gray-800 py-4 rounded-full hover:bg-purple-700 transition-colors text-lg font-semibold`}
        >
          Continue
        </button>
        <FlightDetailsModal
          selectedOffer={flight.selectedOffer}
          isOpen={isFlightModalOpen}
          onClose={() => setIsFlightModalOpen(false)}
        />
      </div>
    </div>
  );
}

export default BookingDetails;
